import { ReactElement } from 'react';
import type { ActivityType } from 'types';
import { CheckField, Form, IntField, SelectField, Submit, TextField, useSubmitHandler } from '@ff-it/form';
import { createOptions } from 'options';
import { Box, DialogBody, DialogClose, DialogFooter, DialogHeader, openModal, useDialogController } from '@ff-it/ui';
import { BlockStatusTemplateField } from 'modules/campaign/status/BlockStatusTemplateField';
import type { PlanConfig } from 'modules/campaign/block/types';
import { ActivityIcon } from 'components/Icon';
import type { KeyedMutator } from 'swr';
import { toast } from 'react-toastify';
import { StatusRemapDialog } from './StatusRemapDialog';
import { strategyLabelMap } from 'modules/campaign/plan/strategy';

type DialogFormProps = { type: ActivityType; initialValues: PlanConfig; endpoint: string; mutate: KeyedMutator<any> };

export function DialogForm({ type, initialValues, endpoint, mutate }: DialogFormProps): ReactElement {
  const dialog = useDialogController();
  const onSubmit = useSubmitHandler(
    { method: 'POST', url: `${endpoint}planning/${type}/` },
    {
      mapValues: async (values: PlanConfig) => {
        // Coerce default to 0

        const currentId =
          initialValues.block_status_template && !initialValues.block_status_template.is_system_default
            ? initialValues.block_status_template.id
            : 0;

        const nextId =
          values.block_status_template && !values.block_status_template.is_system_default
            ? values.block_status_template.id
            : 0;
        if (nextId !== currentId) {
          const remap = await openModal<any>(
            (props) => <StatusRemapDialog currentId={currentId} nextId={nextId} {...props} />,
            {
              dismissProps: { outsidePress: false },
              testId: 'remap-statuses',
            },
          );
          return {
            ...values,
            statuses: remap.statuses,
          };
        }
        return values;
      },
      onSuccess: () => {
        mutate();
        dialog.setOpen(false);
        toast.success('Config has been updated.', { autoClose: 3000 });
      },
    },
  );
  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} display="contents">
      <DialogHeader
        title={
          <>
            <ActivityIcon type={type} className="mr-2" /> {type}
          </>
        }
      />
      <DialogBody>
        <div className="form-row">
          <SelectField
            name="default_plan_strategy"
            label="Default planning strategy"
            options={createOptions(initialValues.available_strategies, strategyLabelMap)}
            required
            className="col"
            simple
          />
        </div>

        <div className="form-row">
          <BlockStatusTemplateField name="block_status_template" className="col" label="Block status template" />
        </div>
        <div className="form-row">
          <CheckField
            name="enabled"
            label="Enabled"
            className="col-6"
            variant="checkbox"
            help="Enable type for planning"
          />
          <CheckField
            name="lock"
            variant="checkbox"
            className="col-6"
            label="Lock strategy"
            help="Prevent strategy toggling"
          />
        </div>
        <Box className="form-row" mt="3" pt="3" bt="default">
          <TextField name="asset_delivery_contact" label="Asset delivery contact" className="col-6" size="sm" />
          <IntField
            name="asset_delivery_deadline"
            className="col-6"
            label="Asset delivery deadline"
            help="Working days before start date"
            size="sm"
          />
        </Box>
      </DialogBody>
      <DialogFooter>
        <DialogClose />
        <Submit ml="auto" />
      </DialogFooter>
    </Form>
  );
}
